document.addEventListener("DOMContentLoaded", () => {
  const hero = document.querySelector("[data-dd-art-hero]");

  if (!hero) return;

  /* ========================================
     HERO ENTRANCE
  ======================================== */

  const revealHero = () => {
    hero.classList.add("dd-art-hero--loaded");
  };

  const loader = document.querySelector("[data-dd-loader]");

  if (!loader || loader.classList.contains("dd-loader--hide")) {
    revealHero();
  } else {
    const loaderObserver = new MutationObserver(() => {
      if (!loader.classList.contains("dd-loader--hide")) return;

      revealHero();
      loaderObserver.disconnect();
    });

    loaderObserver.observe(loader, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  /* ========================================
     FLUID IMAGE REVEAL

     The mask is simulated at a deliberately
     low resolution, then smoothly upscaled.
     A dissipating velocity field turns broad
     strokes into flowing, irregular ribbons
     instead of circular spotlights.
  ======================================== */

  const canvas = hero.querySelector("[data-dd-art-canvas]");
  const imageData = hero.querySelector(".dd-art-hero__image-data");

  if (!canvas || !imageData) return;

  const alternateImageUrl = imageData.dataset.ddImageBack
    ? imageData.dataset.ddImageBack.trim()
    : "";
  const smokeOnly = imageData.dataset.ddSmokeOnly === "true";

  if (!alternateImageUrl && !smokeOnly) {
    console.warn("Throw Co. Art Hero: alternate image missing.");
    return;
  }

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const ctx = canvas.getContext("2d", { alpha: true });
  const imageCanvas = document.createElement("canvas");
  const imageCtx = imageCanvas.getContext("2d", { alpha: false });
  const sceneCanvas = document.createElement("canvas");
  const sceneCtx = sceneCanvas.getContext("2d", { alpha: true });
  const maskCanvas = document.createElement("canvas");
  const maskCtx = maskCanvas.getContext("2d", { alpha: true });
  const edgeCanvas = document.createElement("canvas");
  const edgeCtx = edgeCanvas.getContext("2d", { alpha: true });
  const alternateImage = new Image();

  const autonomousSources = [
    { x: 0.13, y: 0.24, ax: 0.22, ay: 0.16, size: 0.145, speed: 0.31, phase: 0.4, burst: 0.76 },
    { x: 0.53, y: 0.23, ax: 0.25, ay: 0.14, size: 0.165, speed: 0.24, phase: 2.2, burst: 0.63 },
    { x: 0.78, y: 0.66, ax: 0.18, ay: 0.25, size: 0.155, speed: 0.28, phase: 4.1, burst: 0.71 },
    { x: 0.31, y: 0.76, ax: 0.24, ay: 0.16, size: 0.135, speed: 0.35, phase: 5.7, burst: 0.82 }
  ];

  const pointer = {
    active: false,
    opacity: 0,
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
    previousX: 0,
    previousY: 0
  };

  let width = 1;
  let height = 1;
  let dpr = 1;
  let isCompact = false;
  let simWidth = 1;
  let simHeight = 1;
  let field = new Float32Array(1);
  let nextField = new Float32Array(1);
  let maskPixels;
  let edgePixels;
  let noiseA = new Float32Array(1);
  let noiseB = new Float32Array(1);
  let xSin = new Float32Array(1);
  let xCos = new Float32Array(1);
  let ySin = new Float32Array(1);
  let yCos = new Float32Array(1);
  let alternateReady = smokeOnly;
  let heroVisible = true;
  let lastFrame = 0;

  const clamp = (value, minimum, maximum) => {
    return Math.max(minimum, Math.min(maximum, value));
  };

  const smoothstep = (edgeStart, edgeEnd, value) => {
    const normalized = clamp((value - edgeStart) / (edgeEnd - edgeStart), 0, 1);
    return normalized * normalized * (3 - 2 * normalized);
  };

  const getSourcePoint = (source, seconds) => {
    const primary = seconds * source.speed + source.phase;
    const secondary = seconds * source.speed * 0.43 + source.phase * 1.71;

    return {
      x: simWidth * (
        source.x
        + Math.sin(primary) * source.ax
        + Math.sin(secondary) * source.ax * 0.29
      ),
      y: simHeight * (
        source.y
        + Math.cos(primary * 0.81) * source.ay
        + Math.sin(secondary * 1.19) * source.ay * 0.33
      )
    };
  };

  const injectFlow = (centerX, centerY, radius, angle, stretch, strength, phase) => {
    const radiusX = Math.max(2, radius * stretch);
    const radiusY = Math.max(2, radius * (0.66 + Math.sin(phase * 0.73) * 0.09));
    const extent = Math.ceil(Math.max(radiusX, radiusY) * 1.3);
    const minimumX = Math.max(0, Math.floor(centerX - extent));
    const maximumX = Math.min(simWidth - 1, Math.ceil(centerX + extent));
    const minimumY = Math.max(0, Math.floor(centerY - extent));
    const maximumY = Math.min(simHeight - 1, Math.ceil(centerY + extent));
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);

    for (let y = minimumY; y <= maximumY; y += 1) {
      for (let x = minimumX; x <= maximumX; x += 1) {
        const deltaX = x - centerX;
        const deltaY = y - centerY;
        const localX = deltaX * cosine + deltaY * sine;
        const localY = -deltaX * sine + deltaY * cosine;
        const contourNoise =
          Math.sin(localY * 0.32 + phase) * 0.105
          + Math.sin((localX + localY) * 0.17 - phase * 0.63) * 0.065;
        const distance = Math.sqrt(
          (localX * localX) / (radiusX * radiusX)
          + (localY * localY) / (radiusY * radiusY)
        ) + contourNoise;
        const influence = 1 - smoothstep(0.32, 1.08, distance);

        if (influence <= 0) continue;

        const index = y * simWidth + x;
        field[index] = Math.min(1.65, field[index] + influence * strength);
      }
    }
  };

  const seedSimulation = () => {
    const seconds = performance.now() * 0.001;
    const sourceCount = isCompact ? 3 : autonomousSources.length;

    field.fill(0);
    nextField.fill(0);

    autonomousSources.slice(0, sourceCount).forEach((source) => {
      let previousPoint = getSourcePoint(source, seconds - 4.2);

      for (let offset = -4.05; offset <= 0; offset += 0.15) {
        const point = getSourcePoint(source, seconds + offset);
        const deltaX = point.x - previousPoint.x;
        const deltaY = point.y - previousPoint.y;
        const angle = Math.atan2(deltaY, deltaX);
        const radius = Math.min(simWidth, simHeight) * source.size;

        injectFlow(
          point.x,
          point.y,
          radius,
          angle,
          1.65,
          0.075,
          seconds + offset + source.phase
        );

        previousPoint = point;
      }

      source.previousX = previousPoint.x;
      source.previousY = previousPoint.y;
    });
  };

  const buildSimulationBuffers = () => {
    const targetArea = isCompact ? 28000 : 44000;
    const aspectRatio = width / height;

    simWidth = Math.max(92, Math.round(Math.sqrt(targetArea * aspectRatio)));
    simHeight = Math.max(92, Math.round(simWidth / aspectRatio));

    maskCanvas.width = simWidth;
    maskCanvas.height = simHeight;
    edgeCanvas.width = simWidth;
    edgeCanvas.height = simHeight;

    maskCtx.imageSmoothingEnabled = true;
    edgeCtx.imageSmoothingEnabled = true;

    field = new Float32Array(simWidth * simHeight);
    nextField = new Float32Array(simWidth * simHeight);
    noiseA = new Float32Array(simWidth * simHeight);
    noiseB = new Float32Array(simWidth * simHeight);
    xSin = new Float32Array(simWidth);
    xCos = new Float32Array(simWidth);
    ySin = new Float32Array(simHeight);
    yCos = new Float32Array(simHeight);

    for (let x = 0; x < simWidth; x += 1) {
      xSin[x] = Math.sin(x * 0.085);
      xCos[x] = Math.cos(x * 0.085);
    }

    for (let y = 0; y < simHeight; y += 1) {
      ySin[y] = Math.sin(y * 0.092);
      yCos[y] = Math.cos(y * 0.092);
    }

    for (let y = 0; y < simHeight; y += 1) {
      for (let x = 0; x < simWidth; x += 1) {
        const index = y * simWidth + x;
        noiseA[index] = (
          Math.sin(x * 0.31 + Math.sin(y * 0.11) * 2.4)
          + Math.sin(y * 0.27 - x * 0.13) * 0.55
        ) / 1.55;
        noiseB[index] = (
          Math.cos(y * 0.24 + Math.sin(x * 0.14) * 2.1)
          + Math.sin((x + y) * 0.19) * 0.5
        ) / 1.5;
      }
    }

    maskPixels = maskCtx.createImageData(simWidth, simHeight);
    edgePixels = edgeCtx.createImageData(simWidth, simHeight);
    seedSimulation();
  };

  const drawAlternateCover = () => {
    if (!alternateReady || smokeOnly) return;

    imageCtx.setTransform(1, 0, 0, 1, 0, 0);
    imageCtx.clearRect(0, 0, imageCanvas.width, imageCanvas.height);
    imageCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const imageWidth = alternateImage.naturalWidth;
    const imageHeight = alternateImage.naturalHeight;
    const imageRatio = imageWidth / imageHeight;
    const containerRatio = width / height;

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = imageWidth;
    let sourceHeight = imageHeight;

    if (imageRatio > containerRatio) {
      sourceWidth = imageHeight * containerRatio;
      sourceX = (imageWidth - sourceWidth) / 2;
    } else {
      sourceHeight = imageWidth / containerRatio;
      sourceY = (imageHeight - sourceHeight) / 2;
    }

    if (isCompact) {
      const isPhone = width <= 520;
      const bleed = isPhone ? 28 : 22;

      /*
       * A full-bleed, softened copy keeps every reveal connected to the
       * surrounding atmosphere. The complete scene then sits above it with
       * feathered edges, so narrow screens show more of the photograph
       * without exposing the rectangular image boundary.
       */
      imageCtx.save();
      imageCtx.filter = isPhone
        ? "blur(18px) brightness(0.58) saturate(0.78)"
        : "blur(14px) brightness(0.66) saturate(0.82)";
      imageCtx.drawImage(
        alternateImage,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        -bleed,
        -bleed,
        width + bleed * 2,
        height + bleed * 2
      );
      imageCtx.restore();

      imageCtx.fillStyle = isPhone
        ? "rgba(10, 10, 9, 0.16)"
        : "rgba(10, 10, 9, 0.1)";
      imageCtx.fillRect(0, 0, width, height);

      sceneCtx.setTransform(1, 0, 0, 1, 0, 0);
      sceneCtx.clearRect(0, 0, sceneCanvas.width, sceneCanvas.height);
      sceneCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const scale = Math.min(
        (width * (isPhone ? 0.92 : 0.96)) / imageWidth,
        (height * (isPhone ? 0.58 : 0.7)) / imageHeight
      );
      const destinationWidth = imageWidth * scale;
      const destinationHeight = imageHeight * scale;
      const destinationX = (width - destinationWidth) / 2;
      const destinationY = height * (isPhone ? 0.5 : 0.49) - destinationHeight / 2;

      sceneCtx.save();
      sceneCtx.filter = "contrast(1.03) saturate(0.94)";
      sceneCtx.globalAlpha = isPhone ? 0.9 : 0.94;
      sceneCtx.drawImage(
        alternateImage,
        0,
        0,
        imageWidth,
        imageHeight,
        destinationX,
        destinationY,
        destinationWidth,
        destinationHeight
      );
      sceneCtx.restore();

      sceneCtx.globalCompositeOperation = "destination-in";

      const horizontalFeather = sceneCtx.createLinearGradient(
        destinationX,
        0,
        destinationX + destinationWidth,
        0
      );
      horizontalFeather.addColorStop(0, "rgba(0, 0, 0, 0)");
      horizontalFeather.addColorStop(0.09, "rgba(0, 0, 0, 1)");
      horizontalFeather.addColorStop(0.91, "rgba(0, 0, 0, 1)");
      horizontalFeather.addColorStop(1, "rgba(0, 0, 0, 0)");
      sceneCtx.fillStyle = horizontalFeather;
      sceneCtx.fillRect(
        destinationX,
        destinationY,
        destinationWidth,
        destinationHeight
      );

      const verticalFeather = sceneCtx.createLinearGradient(
        0,
        destinationY,
        0,
        destinationY + destinationHeight
      );
      verticalFeather.addColorStop(0, "rgba(0, 0, 0, 0)");
      verticalFeather.addColorStop(0.14, "rgba(0, 0, 0, 1)");
      verticalFeather.addColorStop(0.86, "rgba(0, 0, 0, 1)");
      verticalFeather.addColorStop(1, "rgba(0, 0, 0, 0)");
      sceneCtx.fillStyle = verticalFeather;
      sceneCtx.fillRect(
        destinationX,
        destinationY,
        destinationWidth,
        destinationHeight
      );

      sceneCtx.globalCompositeOperation = "source-over";
      imageCtx.setTransform(1, 0, 0, 1, 0, 0);
      imageCtx.drawImage(sceneCanvas, 0, 0);

      return;
    }

    imageCtx.drawImage(
      alternateImage,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      width,
      height
    );
  };

  const resizeCanvases = () => {
    const rect = hero.getBoundingClientRect();

    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    isCompact = window.matchMedia("(max-width: 900px)").matches;
    dpr = Math.min(window.devicePixelRatio || 1, isCompact ? 1 : 1.15);

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    imageCanvas.width = canvas.width;
    imageCanvas.height = canvas.height;
    sceneCanvas.width = canvas.width;
    sceneCanvas.height = canvas.height;

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.imageSmoothingEnabled = true;
    imageCtx.imageSmoothingEnabled = true;
    sceneCtx.imageSmoothingEnabled = true;

    pointer.x = width * 0.5;
    pointer.y = height * 0.5;
    pointer.targetX = pointer.x;
    pointer.targetY = pointer.y;

    buildSimulationBuffers();
    drawAlternateCover();
  };

  resizeCanvases();

  if ("ResizeObserver" in window) {
    const resizeObserver = new ResizeObserver(resizeCanvases);
    resizeObserver.observe(hero);
  } else {
    window.addEventListener("resize", resizeCanvases, { passive: true });
  }

  if (alternateImageUrl) {
    alternateImage.addEventListener("load", () => {
      alternateReady = true;
      drawAlternateCover();
    });

    alternateImage.addEventListener("error", () => {
      console.error("Throw Co. Art Hero: alternate image failed to load.");
    });

    alternateImage.crossOrigin = "anonymous";
    alternateImage.src = alternateImageUrl;
  }

  const updatePointer = (event) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;

    const rect = hero.getBoundingClientRect();

    pointer.active = true;
    pointer.targetX = event.clientX - rect.left;
    pointer.targetY = event.clientY - rect.top;
  };

  hero.addEventListener("pointerenter", updatePointer, { passive: true });
  hero.addEventListener("pointermove", updatePointer, { passive: true });
  hero.addEventListener("pointerleave", () => {
    pointer.active = false;
  }, { passive: true });

  const advectField = (time, deltaScale) => {
    const timeA = time * 0.00021;
    const timeB = time * 0.00015;
    const cosineA = Math.cos(timeA);
    const sineA = Math.sin(timeA);
    const cosineB = Math.cos(timeB);
    const sineB = Math.sin(timeB);
    const dissipation = Math.pow(0.986, deltaScale);

    for (let y = 0; y < simHeight; y += 1) {
      const flowXFromY = (ySin[y] * cosineA + yCos[y] * sineA) * 0.56;
      const flowYFromY = (yCos[y] * cosineB - ySin[y] * sineB) * 0.19;

      for (let x = 0; x < simWidth; x += 1) {
        const index = y * simWidth + x;
        const flowX = flowXFromY + (xCos[x] * cosineB - xSin[x] * sineB) * 0.24;
        const flowY = (xSin[x] * cosineA + xCos[x] * sineA) * 0.48 - flowYFromY;
        const sourceX = clamp(x - flowX * deltaScale, 0, simWidth - 1.001);
        const sourceY = clamp(y - flowY * deltaScale, 0, simHeight - 1.001);
        const x0 = Math.floor(sourceX);
        const y0 = Math.floor(sourceY);
        const x1 = Math.min(simWidth - 1, x0 + 1);
        const y1 = Math.min(simHeight - 1, y0 + 1);
        const fractionX = sourceX - x0;
        const fractionY = sourceY - y0;
        const top = field[y0 * simWidth + x0] * (1 - fractionX)
          + field[y0 * simWidth + x1] * fractionX;
        const bottom = field[y1 * simWidth + x0] * (1 - fractionX)
          + field[y1 * simWidth + x1] * fractionX;

        nextField[index] = (top * (1 - fractionY) + bottom * fractionY) * dissipation;
      }
    }

    const previousField = field;
    field = nextField;
    nextField = previousField;
  };

  const updateAutonomousFlow = (time, deltaScale) => {
    const seconds = time * 0.001;
    const sourceCount = isCompact ? 3 : autonomousSources.length;
    const smallestSide = Math.min(simWidth, simHeight);

    autonomousSources.slice(0, sourceCount).forEach((source, index) => {
      const point = getSourcePoint(source, seconds);
      const previousX = source.previousX ?? point.x;
      const previousY = source.previousY ?? point.y;
      const deltaX = point.x - previousX;
      const deltaY = point.y - previousY;
      const angle = Math.atan2(deltaY, deltaX);
      const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const pulseWave = Math.max(0, Math.sin(seconds * source.burst + source.phase));
      const burst = Math.pow(pulseWave, 5);
      const radius = smallestSide * source.size * (
        0.92 + Math.sin(seconds * 0.46 + source.phase) * 0.12
      );
      const strength = (0.032 + burst * 0.068) * deltaScale;
      const stretch = 1.55 + Math.min(1.4, movement * 0.45) + burst * 0.65;

      injectFlow(
        point.x,
        point.y,
        radius,
        angle,
        stretch,
        strength,
        seconds + source.phase
      );

      if (burst > 0.22) {
        const side = index % 2 === 0 ? 1 : -1;
        const branchDistance = radius * (0.8 + burst * 0.55);

        injectFlow(
          point.x + Math.cos(angle + Math.PI * 0.5) * branchDistance * side,
          point.y + Math.sin(angle + Math.PI * 0.5) * branchDistance * side,
          radius * 0.64,
          angle + side * 0.65,
          1.38,
          strength * 0.5,
          seconds * 1.17 + source.phase
        );
      }

      source.previousX = point.x;
      source.previousY = point.y;
    });
  };

  const updatePointerFlow = (time, deltaScale) => {
    pointer.x += (pointer.targetX - pointer.x) * 0.12;
    pointer.y += (pointer.targetY - pointer.y) * 0.12;
    pointer.opacity += ((pointer.active ? 1 : 0) - pointer.opacity) * 0.09;

    const currentX = pointer.x / width * simWidth;
    const currentY = pointer.y / height * simHeight;

    if (pointer.previousX === 0 && pointer.previousY === 0) {
      pointer.previousX = currentX;
      pointer.previousY = currentY;
    }

    const deltaX = currentX - pointer.previousX;
    const deltaY = currentY - pointer.previousY;
    const speed = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (pointer.opacity > 0.015 && (pointer.active || speed > 0.08)) {
      const angle = Math.atan2(deltaY, deltaX);
      const radius = Math.min(simWidth, simHeight) * (0.052 + Math.min(0.028, speed * 0.004));
      const stretch = 1.7 + Math.min(2.8, speed * 0.34);
      const strength = (0.018 + Math.min(0.075, speed * 0.014)) * pointer.opacity * deltaScale;

      injectFlow(
        currentX,
        currentY,
        radius,
        angle,
        stretch,
        strength,
        time * 0.0013
      );

      if (speed > 1.4) {
        injectFlow(
          currentX + Math.cos(angle + Math.PI * 0.5) * radius,
          currentY + Math.sin(angle + Math.PI * 0.5) * radius,
          radius * 0.54,
          angle - 0.7,
          1.3,
          strength * 0.48,
          time * 0.0017
        );
      }
    }

    pointer.previousX = currentX;
    pointer.previousY = currentY;
  };

  const renderMask = (time) => {
    const maskData = maskPixels.data;
    const edgeData = edgePixels.data;
    const noiseCosine = Math.cos(time * 0.00019);
    const noiseSine = Math.sin(time * 0.00019);

    for (let index = 0; index < field.length; index += 1) {
      const fieldValue = field[index];
      const movingNoise = noiseA[index] * noiseCosine + noiseB[index] * noiseSine;
      const detail = movingNoise * Math.min(0.072, fieldValue * 0.18);
      const value = fieldValue + detail;
      const alpha = Math.pow(smoothstep(0.07, 0.43, value), 0.86);
      const edge = Math.max(0, 1 - Math.abs(value - 0.185) / 0.105);
      const pixelIndex = index * 4;

      maskData[pixelIndex] = 255;
      maskData[pixelIndex + 1] = 255;
      maskData[pixelIndex + 2] = 255;
      maskData[pixelIndex + 3] = Math.round(alpha * 255);

      edgeData[pixelIndex] = 255;
      edgeData[pixelIndex + 1] = 232;
      edgeData[pixelIndex + 2] = 215;
      edgeData[pixelIndex + 3] = Math.round(edge * (1 - alpha * 0.42) * 112);
    }

    maskCtx.putImageData(maskPixels, 0, 0);
    edgeCtx.putImageData(edgePixels, 0, 0);
  };

  const compositeReveal = () => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;

    if (smokeOnly) {
      ctx.globalCompositeOperation = "screen";
      ctx.globalAlpha = 0.16;
      ctx.filter = `blur(${Math.max(7, Math.round(dpr * 12))}px)`;
      ctx.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 0.98;
      ctx.filter = "none";
      ctx.drawImage(imageCanvas, 0, 0);
      ctx.globalCompositeOperation = "destination-in";
      ctx.globalAlpha = 1;
      ctx.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
    }

    ctx.globalCompositeOperation = "screen";
    ctx.globalAlpha = smokeOnly ? 0.52 : 0.3;
    ctx.filter = `blur(${Math.max(3, Math.round(dpr * 5))}px)`;
    ctx.drawImage(edgeCanvas, 0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = smokeOnly ? 0.42 : 0.22;
    ctx.filter = "none";
    ctx.drawImage(edgeCanvas, 0, 0, canvas.width, canvas.height);

    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.filter = "none";
  };

  const renderReveal = (time, deltaScale) => {
    if (!alternateReady) return;

    advectField(time, deltaScale);
    updateAutonomousFlow(time, deltaScale);
    updatePointerFlow(time, deltaScale);
    renderMask(time);
    compositeReveal();
  };

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    heroVisible = entry.isIntersecting;
  }, {
    rootMargin: "160px 0px"
  });

  visibilityObserver.observe(hero);

  const animate = (time) => {
    const frameInterval = isCompact ? 1000 / 36 : 1000 / 42;
    const elapsed = time - lastFrame;

    if (heroVisible && !document.hidden && elapsed >= frameInterval) {
      lastFrame = time;
      renderReveal(time, Math.min(1.6, Math.max(0.7, elapsed / frameInterval)));
    }

    window.requestAnimationFrame(animate);
  };

  window.requestAnimationFrame(animate);
});
